// src/modules/app-views/customers/pages/Init/modals/CustomersParametersModal/tabs/RequiredFieldsTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useMemo } from 'react';
import { Card, Switch, Flex, Alert, Typography } from 'antd';
import translateX from 'utils/translateX';
import appService from 'services/appService';

const { Text } = Typography;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
// Fields configuration for required fields
// - fieldName: unique identifier for the field
// - label: translation key for display
// - mandatory: if true, always required and disabled (user cannot change)
// - group: 'contact' | 'company' | 'person' | 'address' | 'general'
// - special: 'phoneEmail' for the special toggle that controls phone/email
const FIELDS_CONFIG = [
  // General fields
  {
    fieldName: 'name',
    label: 'name_or_company',
    mandatory: true,
    group: 'general',
  },
  {
    fieldName: 'businessAreaId',
    label: 'business_area',
    mandatory: false,
    group: 'general',
  },
  // Contact fields
  {
    fieldName: 'contactName',
    label: 'contact_name',
    mandatory: false,
    group: 'contact',
  },
  {
    fieldName: 'phone',
    label: 'phone',
    mandatory: false,
    group: 'contact',
  },
  {
    fieldName: 'email',
    label: 'email',
    mandatory: false,
    group: 'contact',
  },
  {
    fieldName: 'phoneEmail',
    label: 'phone_or_email',
    mandatory: false,
    group: 'contact',
    special: 'phoneEmail',
    description: 'phone_or_email_description',
  },
  // Company fields (pessoa jurídica)
  {
    fieldName: 'cnpj',
    label: 'cnpj',
    mandatory: false,
    group: 'company',
  },
  {
    fieldName: 'fullName',
    label: 'full_name_or_company_name',
    mandatory: false,
    group: 'company',
  },
  {
    fieldName: 'foundedAt',
    label: 'founded_at',
    mandatory: false,
    group: 'company',
  },
  // Person fields (pessoa física)
  {
    fieldName: 'cpf',
    label: 'cpf',
    mandatory: false,
    group: 'person',
  },
  {
    fieldName: 'birthdate',
    label: 'birthdate',
    mandatory: false,
    group: 'person',
  },
  // Address fields
  {
    fieldName: 'address',
    label: 'address',
    mandatory: false,
    group: 'address',
    description: 'address_required_description',
  },
];

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const RequiredFieldsTab = ({ loading, setLoading }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [settings, setSettings] = useState(() => {
    // Read form settings (entityPersonType is managed in FormSettingsTab)
    const formData = appService.getModulePreferences({ category: 'form' });
    // Read required fields array directly (now stored as array at module level)
    const fieldsData = appService.getModulePreferences({ category: 'requiredFields' });
    return {
      // Entity type configuration (read-only here, managed in FormSettingsTab)
      entityPersonType: formData?.entityPersonType || 'manual',
      // Required fields array (now stored directly as array)
      fields: Array.isArray(fieldsData) ? fieldsData : ['name'],
    };
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. COMPUTED VALUES
  // ─────────────────────────────────────────────────────────────────────────────
  // Check if phoneEmail is active to disable phone and email switches
  const isPhoneEmailActive = settings.fields.includes('phoneEmail');

  // Build list items with checked state
  const listItems = useMemo(() => {
    return FIELDS_CONFIG.map(field => ({
      ...field,
      checked: field.mandatory || settings.fields.includes(field.fieldName),
      disabled:
        field.mandatory ||
        loading ||
        // Disable phone/email when phoneEmail is active
        (isPhoneEmailActive && (field.fieldName === 'phone' || field.fieldName === 'email')),
    }));
  }, [settings.fields, isPhoneEmailActive, loading]);

  // Group items by category for display
  const groupedItems = useMemo(() => {
    return {
      general: listItems.filter(item => item.group === 'general'),
      contact: listItems.filter(item => item.group === 'contact'),
      company: listItems.filter(item => item.group === 'company'),
      person: listItems.filter(item => item.group === 'person'),
      address: listItems.filter(item => item.group === 'address'),
    };
  }, [listItems]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const saveSettings = useCallback(
    async newFields => {
      setLoading(true);
      try {
        // Save required fields as array directly to requiredFields category
        await appService.updateModulePreferences({
          category: 'requiredFields',
          data: newFields,
        });
        appService.message({ flag: 's', text: 'saved_successfully' });
      } catch (error) {
        console.error('[RequiredFieldsTab] Save error:', error);
        appService.message({ flag: 'e', text: 'error_saving' });
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  const handleToggle = useCallback(
    async (fieldName, checked) => {
      setLoading(true);

      try {
        let updatedFields;

        if (fieldName === 'phoneEmail') {
          // Special handling for phoneEmail toggle
          if (checked) {
            // When phoneEmail is ON, remove phone and email from required, add phoneEmail
            updatedFields = settings.fields.filter(f => f !== 'phone' && f !== 'email');
            updatedFields.push('phoneEmail');
          } else {
            // When phoneEmail is OFF, just remove it
            updatedFields = settings.fields.filter(f => f !== 'phoneEmail');
          }
        } else {
          // Normal toggle behavior
          if (checked) {
            updatedFields = [...settings.fields, fieldName];
          } else {
            updatedFields = settings.fields.filter(f => f !== fieldName);
          }
        }

        // Always include mandatory fields
        const mandatoryFields = FIELDS_CONFIG.filter(f => f.mandatory).map(f => f.fieldName);
        updatedFields = [...new Set([...mandatoryFields, ...updatedFields])];

        setSettings(prev => ({ ...prev, fields: updatedFields }));
        await saveSettings(updatedFields);
      } catch (error) {
        console.error('[RequiredFieldsTab] Toggle error:', error);
        appService.message({ flag: 'e', text: 'error_saving' });
      } finally {
        setLoading(false);
      }
    },
    [settings.fields, saveSettings, setLoading]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. RENDER HELPERS
  // ─────────────────────────────────────────────────────────────────────────────
  const renderFieldsList = items => (
    <Flex vertical gap={0}>
      {items.map((item, index) => (
        <Flex
          key={item.fieldName}
          justify="space-between"
          align="center"
          style={{
            padding: '10px 0',
            borderBottom:
              index < items.length - 1 ? '1px solid var(--ant-color-border-secondary)' : 'none',
          }}
        >
          <Flex vertical gap={2}>
            <Text style={{ fontSize: 13 }}>
              {translateX(item.label)}
              {item.mandatory && (
                <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                  ({translateX('mandatory')})
                </Text>
              )}
            </Text>
            {item.description && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                {translateX(item.description)}
              </Text>
            )}
          </Flex>
          <Switch
            checked={item.checked}
            disabled={item.disabled}
            onChange={checked => handleToggle(item.fieldName, checked)}
            size="small"
          />
        </Flex>
      ))}
    </Flex>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div
      className="no-scrollbar"
      style={{
        height: 'calc(80vh - 240px)',
        overflowY: 'auto',
        overflowX: 'hidden',
        // paddingRight: 8,
        // paddingBottom: 16,
      }}
    >
      <Flex vertical gap={16}>
        <Alert
          type="info"
          showIcon
          title={translateX('required_fields_info_title')}
          description={translateX('required_fields_info_description')}
        />

        {/* General Fields */}
        {/*<Card title={translateX('general')} size="small">*/}
        {/*  {renderFieldsList(groupedItems.general)}*/}
        {/*</Card>*/}

        {/* Company Fields (Pessoa Jurídica) - only show if not person-only mode */}
        {settings.entityPersonType !== 'person' && (
          <Card title={translateX('company_legal_entity')} size="small">
            {renderFieldsList(groupedItems.company)}
          </Card>
        )}

        {/* Person Fields (Pessoa Física) - only show if not company-only mode */}
        {settings.entityPersonType !== 'company' && (
          <Card title={translateX('individual_person')} size="small">
            {renderFieldsList(groupedItems.person)}
          </Card>
        )}

        {/* Contact Fields */}
        <Card title={translateX('contact')} size="small">
          {renderFieldsList(groupedItems.contact)}
          {isPhoneEmailActive && (
            <Alert
              type="warning"
              showIcon
              title={translateX('phone_email_mode_active')}
              description={translateX('phone_email_mode_description')}
              style={{ marginTop: 8 }}
            />
          )}
        </Card>

        {/* Address Fields */}
        <Card title={translateX('address')} size="small">
          {renderFieldsList(groupedItems.address)}
        </Card>
      </Flex>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default RequiredFieldsTab;
